import { Button } from '@repo/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@repo/ui/card'
import { useAsyncData } from '@repo/ui/dataProvider'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Switch } from '@repo/ui/switch'
import { Textarea } from '@repo/ui/textarea'
import { useMemo } from 'react'

import { adminStoreActions } from '@/store/actions'
import { getCurrentOrganization } from '@/store/helpers'
import { useStoreDispatch } from '@/store/hooks'
import { organizationSchema } from '@/validation-schemas'

export type ChatMessageSettingsType = {
  chatAssistantName?: string | undefined
  chatAnswerGoal?: string | undefined
  chatMessageInstructions?: string | undefined
  chatSafetyRules?: string | undefined
}

interface ChatMessageSettingsProps {
  initialValues: {
    assistantName: string
    answerGoal: string
    messageInstructions: string
    safetyRules: string
  }
  onChatMessageSettingsUpdated: (chatMessageSettings: ChatMessageSettingsType) => void
}

export function ChatMessageSettings({
  initialValues,
  onChatMessageSettingsUpdated,
}: ChatMessageSettingsProps) {
  const { t } = useT({ keyPrefix: 'admin.organization.chatMessageSettings' })

  const defaultValues = useMemo(() => initialValues, [initialValues])

  return (
    <Card className="flex flex-1 flex-col overflow-hidden overflow-y-auto">
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <ChatMessageSettingsForm
        defaultValues={defaultValues}
        onChatMessageSettingsUpdated={onChatMessageSettingsUpdated}
      />
      <AllowFeedback />
    </Card>
  )
}
function AllowFeedback() {
  const dispatch = useStoreDispatch()
  const {
    data: [organization],
  } = useAsyncData([getCurrentOrganization])
  if (!organization) return null
  const isAllowed = organization.chatSettings?.allowFeedback ?? false
  const handleChange = (checked: boolean) => {
    if (checked === isAllowed) return
    if (!organization?.chatSettings) return
    void dispatch(
      adminStoreActions.organization.update({
        fields: {
          chatSettings: {
            ...organization.chatSettings,
            allowFeedback: checked,
          },
        },
      }),
    )
  }
  return (
    <Card className="mx-4 mb-6 xl:mx-6">
      <CardContent>
        <div className="flex items-center space-x-2">
          <Switch checked={isAllowed} onCheckedChange={handleChange} />
          <div>Feedback {isAllowed ? 'enabled' : 'disabled'}</div>
        </div>
      </CardContent>
    </Card>
  )
}

function ChatMessageSettingsForm({
  defaultValues,
  onChatMessageSettingsUpdated,
}: {
  defaultValues: {
    assistantName: string
    messageInstructions: string
    answerGoal: string
    safetyRules: string
  }
  onChatMessageSettingsUpdated: (chatMessageSettings: ChatMessageSettingsType) => void
}) {
  const { t, tc } = useT({
    keyPrefix: 'admin.organization.chatMessageSettings',
  })

  const { form, isFormValid, submit, submitOnPressMetaEnter } = useFormValidator({
    defaultValues,
    onSubmit: (values) => {
      const chatSettings: ChatMessageSettingsType = {}
      if (values.assistantName !== defaultValues.assistantName)
        chatSettings.chatAssistantName = values.assistantName
      if (values.answerGoal !== defaultValues.answerGoal)
        chatSettings.chatAnswerGoal = values.answerGoal
      if (values.messageInstructions !== defaultValues.messageInstructions)
        chatSettings.chatMessageInstructions = values.messageInstructions
      if (values.safetyRules !== defaultValues.safetyRules)
        chatSettings.chatSafetyRules = values.safetyRules
      onChatMessageSettingsUpdated(chatSettings)
    },
    schema: {
      assistantName: organizationSchema.assistantName,
      answerGoal: organizationSchema.answerGoal,
      messageInstructions: organizationSchema.messageInstructions,
      safetyRules: organizationSchema.safetyRules,
    },
  })
  return (
    <Form {...form}>
      <form onSubmit={submit} onKeyDown={submitOnPressMetaEnter} className="flex flex-1 flex-col">
        <CardContent className="space-y-4">
          <FormField
            control={form.control}
            name="assistantName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('assistantName')}</FormLabel>
                <FormControl>
                  <Input placeholder={t('defaultValuePlaceholder')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="messageInstructions"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('messageInstructions')}</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t('defaultValuePlaceholder')}
                    {...field}
                    className="h-40 resize-none"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="answerGoal"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>{t('answerGoal')}</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t('defaultValuePlaceholder')}
                    {...field}
                    className="h-60 resize-none"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="safetyRules"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>{t('safetyRules')}</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t('defaultValuePlaceholder')}
                    {...field}
                    className="h-60 resize-none"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>

        <CardFooter className="justify-end">
          <Button type="submit" disabled={!isFormValid}>
            {tc('update', { cfl: true })}
          </Button>
        </CardFooter>
      </form>
    </Form>
  )
}
